import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/* ==================================================================
   TabNav
   ------------------------------------------------------------------
   A tab strip whose active underline TRAVELS to the newly selected
   tab instead of being repainted under it. The effect is a shared
   layout animation done with one measurement: the active button's
   offset and width are written to the strip as CSS custom properties
   and a single absolutely-positioned bar transitions between them.

   Re-measures on resize, on font load, and whenever the labels change
   (badge counts arriving late would otherwise leave the ink stale).
   ================================================================== */

export default function TabNav({
  items,
  active,
  onChange,
  className = '',
  tabClassName = '',
  ariaLabel = 'Sections',
}) {
  const stripRef = useRef(null);
  const tabsRef = useRef(new Map());
  const [ink, setInk] = useState({ x: 0, w: 0, ready: false });

  const measure = useCallback(() => {
    const strip = stripRef.current;
    const node = tabsRef.current.get(active);
    if (!strip || !node) return;
    const x = node.offsetLeft - strip.scrollLeft;
    const w = node.offsetWidth;
    /* Bail out when nothing moved. `items` is a fresh array on every render,
       so this effect re-runs constantly; returning the previous object lets
       React skip the re-render instead of looping. */
    setInk(prev => (prev.ready && prev.x === x && prev.w === w) ? prev : { x, w, ready: true });
  }, [active]);

  useLayoutEffect(measure, [measure, items]);

  useEffect(() => {
    const strip = stripRef.current;
    window.addEventListener('resize', measure);
    strip?.addEventListener('scroll', measure, { passive: true });
    if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});
    return () => {
      window.removeEventListener('resize', measure);
      strip?.removeEventListener('scroll', measure);
    };
  }, [measure]);

  return (
    <div
      ref={stripRef}
      className={`ui-tabs ${className}`.trim()}
      role="tablist"
      aria-label={ariaLabel}
      style={{ '--ui-ink-x': `${ink.x}px`, '--ui-ink-w': `${ink.w}px` }}
    >
      {items.map(({ id, label, icon, badge, badgeTone }) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={active === id}
          ref={(node) => {
            if (node) tabsRef.current.set(id, node);
            else tabsRef.current.delete(id);
          }}
          className={`${tabClassName}${active === id ? ' active' : ''}`}
          onClick={() => onChange(id)}
        >
          {icon}
          <span>{label}</span>
          {badge != null && badge !== 0 && (
            <span className={`ui-tab-badge${badgeTone ? ` ui-tab-badge--${badgeTone}` : ''}`}>{badge}</span>
          )}
        </button>
      ))}
      <span className="ui-tabs__ink" data-ready={ink.ready ? 'true' : 'false'} />
    </div>
  );
}
